import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { whiskies } from '@/mocks/products';
import ProductCard from './ProductCard';
import useCarousel from '@/hooks/useCarousel';

const WhiskiesSection = () => {
  const { t } = useTranslation();
  const { currentIndex, containerRef, trackStyle, itemStyle, prev, next, goTo, canPrev, canNext, dotsCount, pauseAutoplay, resumeAutoplay } = useCarousel({
    total: whiskies.length,
    visibleCards: 3,
    gap: 24,
    autoplay: true,
    autoplayInterval: 4500,
  });

  const regionStrips = [
    { name: 'Islay', desc: t('whiskies_islay_desc'), icon: 'ri-fire-line', link: '/the-estate' },
    { name: 'Speyside', desc: t('whiskies_speyside_desc'), icon: 'ri-leaf-line', link: '/speyside' },
    { name: 'Highlands', desc: t('whiskies_highlands_desc'), icon: 'ri-landscape-line', link: '/descubra-as-highlands' },
  ];

  return (
    <section className="relative py-24 px-6 md:px-10 xl:px-16 overflow-hidden" data-product-shop>
      <div
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: "url('https://readdy.ai/api/search-image?query=dark%20moody%20whisky%20barrels%20aging%20warehouse%20rickhouse%20oak%20casks%20dramatic%20lighting%20amber%20glow%20premium%20scotch%20distillery%20atmospheric%20cinematic%20photography&width=1600&height=900&seq=whisky-bg&orientation=landscape')",
        }}
      />
      <div className="absolute inset-0 bg-[#0a0a0a]/90" />

      <div className="relative z-10 max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-4">
          <div>
            <p className="text-[#c9a84c] text-xs tracking-[0.3em] uppercase mb-3 font-medium">{t('whiskies_label')}</p>
            <h2
              className="text-4xl md:text-5xl font-light text-white"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {t('whiskies_title')}
            </h2>
            <p className="text-white/40 text-sm mt-3 max-w-md font-light">
              {t('whiskies_subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="flex items-center gap-3">
              <button
                onClick={prev}
                disabled={!canPrev}
                className="w-10 h-10 flex items-center justify-center border border-white/20 rounded-full text-white/60 hover:border-[#c9a84c] hover:text-[#c9a84c] transition-all duration-300 disabled:opacity-25 disabled:cursor-not-allowed cursor-pointer"
                aria-label="Previous"
              >
                <i className="ri-arrow-left-line text-sm"></i>
              </button>
              <button
                onClick={next}
                disabled={!canNext}
                className="w-10 h-10 flex items-center justify-center border border-white/20 rounded-full text-white/60 hover:border-[#c9a84c] hover:text-[#c9a84c] transition-all duration-300 disabled:opacity-25 disabled:cursor-not-allowed cursor-pointer"
                aria-label="Next"
              >
                <i className="ri-arrow-right-line text-sm"></i>
              </button>
            </div>
            <Link
              to="/whiskies"
              className="flex items-center gap-2 border border-[#c9a84c]/40 hover:border-[#c9a84c] text-[#c9a84c] text-xs tracking-widest uppercase font-medium px-6 py-3 rounded-md transition-all whitespace-nowrap"
            >
              {t('whiskies_see_all')}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div
          onMouseEnter={pauseAutoplay}
          onMouseLeave={resumeAutoplay}
        >
          <div ref={containerRef} className="overflow-hidden">
            <div className="flex" style={trackStyle}>
              {whiskies.map(product => (
                <div key={product.id} style={itemStyle}>
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center gap-2 mt-10">
            {Array.from({ length: dotsCount }).map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  i === currentIndex ? 'w-8 h-1.5 bg-[#c9a84c]' : 'w-1.5 h-1.5 bg-white/20 hover:bg-white/40'
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
          {regionStrips.map(region => (
            <div key={region.name} className="bg-white/5 border border-white/8 rounded-xl p-6 hover:border-[#c9a84c]/30 transition-all cursor-pointer group">
              <div className="w-10 h-10 flex items-center justify-center mb-4">
                <i className={`${region.icon} text-[#c9a84c] text-2xl`}></i>
              </div>
              <p className="text-white text-xl font-light mb-2 group-hover:text-[#c9a84c] transition-colors" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{region.name}</p>
              <p className="text-white/40 text-xs leading-relaxed mb-4">{region.desc}</p>
              <a href={region.link} className="text-[#c9a84c]/60 text-[10px] tracking-widest uppercase hover:text-[#c9a84c] transition-colors flex items-center gap-1">
                {t('whiskies_discover')} <i className="ri-arrow-right-line text-xs"></i>
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhiskiesSection;
